import { createContext, useContext, useState, useEffect } from 'react';
import '../components/scss/CartModal.scss';
import CartModal from '../components/CartModal';
import { useNavigate } from 'react-router-dom';
import { useBackButtonManager } from './BackButtonContext';
import Button from '../components/ui/Button';
import { useCurrency } from './CurrencyContext';

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const { cny } = useCurrency();
  const [cartItems, setCartItems] = useState(() => {
    const saved = localStorage.getItem("cartItems");
    return saved ? JSON.parse(saved) : [];
  });
  const { actionPreview, returnPreview } = useBackButtonManager();  
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isEnable, setIsEnable] = useState(true);

  useEffect(() => {
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (item) => {
    setCartItems((prev) => {
      const existing = prev.find((i) => i.id === item.id);
      if (existing) {
        return prev.map((i) =>
          i.id === item.id ? { ...i, quantity: i.quantity + item.quantity } : i
        );
      }
      return [...prev, item];
    });
  };

  const clearCart = () => {
    setCartItems([]);
  }

  const removeFromCart = (id) => {
    setCartItems((prev) => prev.filter((i) => i.id !== id));
  };

  const updateQuantity = (id, quantity) => {
    setCartItems((prev) =>
      prev.map((i) =>
        i.id === id ? { ...i, quantity: Math.max(1, quantity) } : i
      )
    );
  };

  const openModal = () => {
    actionPreview(() => {
      setIsCartOpen(false);
      returnPreview();
    });
    setIsCartOpen(true);
  }

  const totalCount = cartItems.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        totalCount,
        isCartOpen,
        setIsCartOpen,
        setIsEnable,
        openModal,
        clearCart
      }}
    >
      {children}
      {(cartItems.length > 0 && isEnable) ? (
          <Button className='cart-badge btn-primary' onClick={() => openModal()}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.1273 2.58786C12.5132 2.39489 12.9771 2.52975 13.2031 2.88652L13.2455 2.96058L15.5152 7.49996H17.5C17.9603 7.49996 18.3334 7.87306 18.3334 8.3333C18.3334 8.79353 17.9603 9.16663 17.5 9.16663H17.3495L16.246 14.6867C16.172 15.0563 16.1085 15.378 16.0352 15.6412C15.9781 15.8462 15.907 16.0471 15.8016 16.2402L15.6836 16.4306C15.4529 16.7612 15.146 17.0302 14.7901 17.2159L14.6346 17.2908C14.3553 17.4128 14.0745 17.4594 13.7915 17.4804C13.519 17.5007 13.1911 17.5 12.8141 17.5H7.18589C6.80897 17.5 6.48101 17.5007 6.20852 17.4804C5.99634 17.4647 5.78537 17.4344 5.57538 17.3689L5.36542 17.2908C4.99607 17.1294 4.67167 16.8815 4.41978 16.569L4.31643 16.4306C4.142 16.1807 4.04099 15.9146 3.96486 15.6412C3.92819 15.5095 3.89426 15.3631 3.85988 15.2034L3.75409 14.6867L2.65057 9.16663H2.50002C2.03978 9.16663 1.66669 8.79353 1.66669 8.3333C1.66669 7.87306 2.03978 7.49996 2.50002 7.49996H4.48488L6.75458 2.96058L6.7969 2.88652C7.02299 2.52975 7.48682 2.39489 7.87274 2.58786C8.25868 2.78082 8.42943 3.23295 8.27964 3.62789L8.24546 3.70602L6.34849 7.49996H13.6516L11.7546 3.70602L11.7204 3.62789C11.5706 3.23295 11.7414 2.78082 12.1273 2.58786ZM5.3882 14.3595L5.48912 14.8535C5.51824 14.9891 5.54406 15.0995 5.5705 15.1945C5.62044 15.3738 5.65796 15.4412 5.6828 15.4769L5.75523 15.5664C5.83289 15.6502 5.9273 15.7172 6.03274 15.7633L6.12714 15.7918C6.17376 15.8016 6.23893 15.8109 6.33221 15.8178C6.52871 15.8324 6.78456 15.8333 7.18589 15.8333H12.8141C13.2155 15.8333 13.4713 15.8324 13.6678 15.8178C13.8539 15.804 13.9275 15.7807 13.9673 15.7633L14.069 15.7104C14.1665 15.6507 14.2513 15.5713 14.3172 15.4769L14.3636 15.3898C14.3823 15.346 14.4045 15.2843 14.4295 15.1945C14.4824 15.0046 14.5331 14.7533 14.6118 14.3595L15.6502 9.16663H4.34979L5.3882 14.3595ZM6.66669 13.3333V10.8333C6.66669 10.3731 7.03978 9.99996 7.50002 9.99996C7.96026 9.99996 8.33335 10.3731 8.33335 10.8333V13.3333C8.33335 13.7935 7.96026 14.1666 7.50002 14.1666C7.03978 14.1666 6.66669 13.7935 6.66669 13.3333ZM9.16669 13.3333V10.8333C9.16669 10.3731 9.53978 9.99996 10 9.99996C10.4603 9.99996 10.8334 10.3731 10.8334 10.8333V13.3333C10.8334 13.7935 10.4603 14.1666 10 14.1666C9.53978 14.1666 9.16669 13.7935 9.16669 13.3333ZM11.6667 13.3333V10.8333C11.6667 10.3731 12.0398 9.99996 12.5 9.99996C12.9603 9.99996 13.3334 10.3731 13.3334 10.8333V13.3333C13.3334 13.7935 12.9603 14.1666 12.5 14.1666C12.0398 14.1666 11.6667 13.7935 11.6667 13.3333Z" fill="#1D1D20"/>
              </svg>
            {cartItems.length} товар{cartItems.length > 1 ? 'а' : ''}
          </Button>
      ) : (<></>)}
      {isCartOpen && <CartModal />}
    </CartContext.Provider>
  );
};
